'use client';

import React, { useCallback, useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useSession } from 'next-auth/react';
import { Loader2, Binoculars } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn as cnJoin } from '@/lib/utils';
import { apiFetch } from '@/lib/api';
import { track } from '@/lib/analytics';
import { trackEvent } from '@/lib/tracking';

type FollowState = {
  followerCount: number;
  followingCount: number;
  isFollowing: boolean;
};

type Props = {
  /** Target person to follow (People.id, INT). */
  personId: number | string;
  /** Hide the button when viewing your own profile (caller passes true when personId === me). */
  isSelf?: boolean;
  /** Show the public follower count next to the button. */
  showCount?: boolean;
  /** Optional: receive count updates after toggles. */
  onCountChange?: (followerCount: number) => void;
  /** 'scrim' renders a translucent white-on-image pill for use inside the profile banner. */
  appearance?: 'default' | 'scrim';
  className?: string;
};

export default function FollowButton({ personId, isSelf = false, showCount = true, onCountChange, appearance = 'default', className = '' }: Props) {
  const router = useRouter();
  const { status } = useSession();
  const authed = status === 'authenticated';
  const [state, setState] = useState<FollowState | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await apiFetch(`/api/follow/${encodeURIComponent(String(personId))}`, { method: 'GET', cache: 'no-store' });
      if (!res.ok) return;
      const j: any = await res.json().catch(() => null);
      if (!j) return;
      setState({
        followerCount: Number(j.followerCount) || 0,
        followingCount: Number(j.followingCount) || 0,
        isFollowing: Boolean(j.isFollowing),
      });
    } catch { /* counts are decorative — silent fail is acceptable */ }
  }, [personId]);

  useEffect(() => { load(); }, [load]);

  const toggle = useCallback(async () => {
    if (busy || isSelf) return;
    if (!authed) {
      // /register, not /login: guests here are mostly first-time visitors from
      // search; the goal is signups. /register keeps a "Sign in" link for
      // returning users and returnTo lands them back on this profile.
      const ret = typeof window !== 'undefined' ? window.location.pathname : '/';
      router.push(`/register?returnTo=${encodeURIComponent(ret)}`);
      return;
    }
    setBusy(true);
    setError(null);
    const wasFollowing = state?.isFollowing ?? false;
    // Optimistic update
    setState(prev => prev ? { ...prev, isFollowing: !wasFollowing, followerCount: prev.followerCount + (wasFollowing ? -1 : 1) } : prev);
    try {
      const res = await apiFetch(`/api/follow/${encodeURIComponent(String(personId))}`, {
        method: wasFollowing ? 'DELETE' : 'POST',
        cache: 'no-store',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const j: any = await res.json().catch(() => null);
      if (j && typeof j.followerCount === 'number') {
        setState(prev => prev ? { ...prev, isFollowing: Boolean(j.isFollowing), followerCount: Number(j.followerCount) || 0 } : prev);
        onCountChange?.(Number(j.followerCount) || 0);
      }
      try { track(wasFollowing ? 'follow_removed' : 'follow_added', { personId: String(personId) }); } catch {}
      try {
        if (!wasFollowing) {
          trackEvent('follow_added', { subject_person_id: String(personId) });
        }
      } catch {}
    } catch (e: any) {
      // Roll back optimistic state
      setState(prev => prev ? { ...prev, isFollowing: wasFollowing, followerCount: prev.followerCount + (wasFollowing ? 1 : -1) } : prev);
      setError(e?.message || 'Could not update follow state');
    } finally {
      setBusy(false);
    }
  }, [authed, busy, isSelf, onCountChange, personId, router, state?.isFollowing]);

  if (isSelf) return null;

  const isFollowing = state?.isFollowing ?? false;
  const followerCount = state?.followerCount;
  const scrim = appearance === 'scrim';

  return (
    <div className="inline-flex items-center gap-2">
      <Button
        type="button"
        variant={scrim ? 'ghost' : isFollowing ? 'secondary' : 'primary'}
        size="sm"
        onClick={toggle}
        disabled={busy}
        aria-pressed={isFollowing}
        aria-label={isFollowing ? 'Stop watching' : 'Watch'}
        className={cnJoin(
          'inline-flex items-center gap-1.5',
          scrim && 'bg-white/10 text-white ring-1 ring-white/25 hover:bg-white/20 hover:text-white backdrop-blur-sm',
          scrim && isFollowing && 'bg-white/25',
          className
        )}
      >
        {busy ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
        ) : (
          <Binoculars className="h-3.5 w-3.5" aria-hidden="true" />
        )}
        <span>{isFollowing ? 'Watching' : 'Watch'}</span>
      </Button>
      {showCount && typeof followerCount === 'number' && (
        <span className="text-xs text-muted-foreground" aria-label={`${followerCount} watchers`}>
          {followerCount.toLocaleString()} {followerCount === 1 ? 'watcher' : 'watchers'}
        </span>
      )}
      {error && (
        <span className="sr-only" role="status">{error}</span>
      )}
    </div>
  );
}
